import * as Result from "@gordonb/result/result";
import { renderRoute } from "../../util/route.ts";
import { type Ulid } from "../../models/ulid.ts";
import {
  type ConversationMessagesResult,
  ConversationMessagesResultSchema,
  ConversationSchema,
  type CreateConversation,
  type UpsertConversation,
} from "../../models/conversation.ts";
import { toBearerToken } from "../../util/bearer-token.ts";
import { DeleteResultSchema } from "../../models/delete-result.ts";
import { ResultSchemaOf } from "../../models/result.ts";
import { readUIMessageStream } from "ai";
import { streamJsonLines } from "../../util/stream.ts";
import {
  type ChatMessage,
  type ChatMessageChunk,
  type UserChatMessage,
} from "../../models/chat-message.ts";
import {
  CONVERSATION_MESSAGES_API_URL,
  CONVERSATIONS_API_URL,
  CREATE_CONVERSATION_API_URL,
  MESSAGE_API_URL,
  MESSAGES_API_URL,
  PROJECT_CONVERSATIONS_API_URL,
} from "../client-config.ts";
import * as z from "zod/v4";

const ConversationResultSchema = ResultSchemaOf(ConversationSchema, z.string());
const ConversationArrayResultSchema = ResultSchemaOf(
  z.array(ConversationSchema),
  z.string(),
);
const UpdateResultSchema = ResultSchemaOf(
  z.object({ updated: z.boolean() }),
  z.string(),
);

export type ConversationResult = z.infer<typeof ConversationResultSchema>;
export type ConversationArrayResult = z.infer<
  typeof ConversationArrayResultSchema
>;

/**
 * Create a new conversation
 */
export const createConversation = async (
  token: string | undefined,
  conversation: CreateConversation,
): Promise<ConversationResult> => {
  if (!token) {
    return Result.err("Authorization token required");
  }

  try {
    const response = await fetch(CREATE_CONVERSATION_API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        authorization: toBearerToken(token),
      },
      body: JSON.stringify(conversation),
    });
    const json = await response.json();
    return ConversationResultSchema.parse(json);
  } catch (error) {
    return Result.err((error as Error).message);
  }
};

/**
 * Get a conversation by ID
 */
export const getConversation = async (
  token: string | undefined,
  conversationId: Ulid,
): Promise<ConversationResult> => {
  if (!token) {
    return Result.err("Authorization token required");
  }

  const url = renderRoute(CONVERSATIONS_API_URL, {
    conversation_id: conversationId,
  });

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        authorization: toBearerToken(token),
      },
    });
    const json = await response.json();
    return ConversationResultSchema.parse(json);
  } catch (error) {
    return Result.err((error as Error).message);
  }
};

/**
 * List conversations for an agent
 */
export const listProjectConversations = async (
  token: string | undefined,
  projectId: Ulid,
): Promise<ConversationArrayResult> => {
  if (!token) {
    return Result.err("Authorization token required");
  }

  const url = renderRoute(PROJECT_CONVERSATIONS_API_URL, {
    project_id: projectId,
  });

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        authorization: toBearerToken(token),
      },
    });
    const json = await response.json();
    return ConversationArrayResultSchema.parse(json);
  } catch (error) {
    return Result.err((error as Error).message);
  }
};

/**
 * Upsert a conversation
 */
export const upsertConversation = async (
  token: string | undefined,
  conversationId: Ulid,
  conversation: UpsertConversation,
): Promise<ConversationResult> => {
  if (!token) {
    return Result.err("Authorization token required");
  }

  const url = renderRoute(CONVERSATIONS_API_URL, {
    conversation_id: conversationId,
  });

  try {
    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        authorization: toBearerToken(token),
      },
      body: JSON.stringify(conversation),
    });
    const json = await response.json();
    return ConversationResultSchema.parse(json);
  } catch (error) {
    return Result.err((error as Error).message);
  }
};

/**
 * Delete a conversation
 */
export const deleteConversation = async (
  token: string | undefined,
  conversationId: Ulid,
): Promise<z.infer<typeof DeleteResultSchema>> => {
  if (!token) {
    return Result.err("Authorization token required");
  }

  const url = renderRoute(CONVERSATIONS_API_URL, {
    conversation_id: conversationId,
  });

  try {
    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        authorization: toBearerToken(token),
      },
    });
    const json = await response.json();
    return DeleteResultSchema.parse(json);
  } catch (error) {
    return Result.err((error as Error).message);
  }
};

/**
 * Get messages for a conversation
 */
export const getMessages = async (
  token: string | undefined,
  conversationId: Ulid,
): Promise<ConversationMessagesResult> => {
  if (!token) {
    return Result.err("Authorization token required");
  }

  const url = renderRoute(CONVERSATION_MESSAGES_API_URL, {
    conversation_id: conversationId,
  });

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        authorization: toBearerToken(token),
      },
    });
    const json = await response.json();
    return ConversationMessagesResultSchema.parse(
      json,
    ) as ConversationMessagesResult;
  } catch (error) {
    return Result.err((error as Error).message);
  }
};

/**
 * Post a user message to a conversation
 */
export const postUserMessage = async (
  token: string | undefined,
  message: UserChatMessage,
): Promise<ReadableStream<ChatMessage>> => {
  if (!token) {
    throw new Error("Authorization token required");
  }

  const response = await fetch(MESSAGES_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      authorization: toBearerToken(token),
    },
    body: JSON.stringify(message),
  });

  if (response.body == undefined) throw new Error("No body");

  const jsonChunkStream = streamJsonLines(response.body);

  return readUIMessageStream({
    stream: jsonChunkStream as ReadableStream<ChatMessageChunk>,
  }) as ReadableStream<ChatMessage>;
};

/**
 * Save thinking summaries to message metadata
 */
export const saveThinkingSummaries = async (
  token: string | undefined,
  messageId: string,
  summaries: string[],
): Promise<z.infer<typeof UpdateResultSchema>> => {
  if (!token) {
    return Result.err("Authorization token required");
  }

  const url = renderRoute(MESSAGE_API_URL, { message_id: messageId });

  try {
    const response = await fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        authorization: toBearerToken(token),
      },
      body: JSON.stringify({ metadata: { thinking_summaries: summaries } }),
    });
    const json = await response.json();
    return UpdateResultSchema.parse(json);
  } catch (error) {
    return Result.err((error as Error).message);
  }
};
